import { useMemo } from "react";
import {
  DownloadSimple, ArrowSquareOut, Play, Package, InstagramLogo, FilmSlate,
  IdentificationBadge, Folders, FilePdf,
} from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";

/**
 * Admin-only Downloads & Resources.
 *
 * Every entry links DIRECTLY to a file that already exists in
 * /app/frontend/public/. Nothing is regenerated, moved or renamed.
 * The panel itself lives inside /admin (behind X-Admin-Token gate) —
 * NOT wired into public navigation, sitemap, or any public page.
 */

const BRAND_KITS = [
  { title: "Core Sweat With Slay Brand Kit", url: "/sweat-with-slay-brand-kit.zip" },
  { title: "Sweat With Slay Legacy Brand Kit", url: "/sweat-with-slay-legacy-brand-kit.zip" },
];

const IG_CAROUSELS = [
  { title: "IG Carousel — Legacy Feed", url: "/carousel-legacy.zip" },
  { title: "IG Carousel — Legacy Stories", url: "/carousel-legacy-story.zip" },
  { title: "IG Carousel — About", url: "/carousel-about.zip" },
  { title: "IG Carousel — FAQ", url: "/carousel-faq.zip" },
  { title: "IG Carousel — Programs", url: "/carousel-programs.zip" },
  { title: "Sweat With Slay Instagram Pack", url: "/brand/sweat-with-slay-instagram-pack.zip" },
];

// Business Materials: filed under professional / brand deliverables
const BUSINESS_MATERIALS = [
  { title: "Sweat With Slay Brand Guidelines", url: "/merch/sws-brand-guidelines.pdf", filetype: "PDF" },
];

// Other private resources found in /app/frontend/public that are NOT part of
// public nav but are still legitimate business assets.
const OTHER_PRIVATE_RESOURCES = [
  { title: "Printful Assets Bundle", url: "/printful-assets.zip", filetype: "ZIP" },
  { title: "Merch Pack (PNG mockups)", url: "/merch/sws-merch-pack.zip", filetype: "ZIP" },
  { title: "Merch Pack (SVG source)", url: "/merch/sws-merch-pack-svg.zip", filetype: "ZIP" },
  { title: "Merch Pack (all formats)", url: "/merch/sws-merch-pack-all.zip", filetype: "ZIP" },
  { title: "Press Reel — Who is AJ", url: "/assets/press/reel-01-who.mp4", filetype: "MP4" },
  { title: "Press Reel — Timmy tribute", url: "/assets/press/reel-02-timmy.mp4", filetype: "MP4" },
  { title: "Press Reel — 911-minute challenge", url: "/assets/press/reel-03-challenge.mp4", filetype: "MP4" },
];

function ZipRow({ item }) {
  const filename = item.url.split("/").pop();
  return (
    <div
      data-testid={`download-zip-${filename}`}
      className="flex flex-wrap items-center gap-3 border border-white/10 bg-black px-4 py-3"
    >
      <div className="flex-1 min-w-[200px]">
        <p className="text-white text-sm font-bold leading-tight">{item.title}</p>
        <p className="text-white/40 text-[10px] font-mono mt-0.5">
          {item.filetype ? `${item.filetype} · ` : ""}{item.url}
        </p>
      </div>
      <a
        href={item.url}
        target="_blank"
        rel="noreferrer noopener"
        data-testid={`download-zip-open-${filename}`}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold border border-white/25 hover:border-[#FF1B91] text-white/80 hover:text-white px-3 py-2"
      >
        <ArrowSquareOut size={12} weight="bold" /> Open
      </a>
      <a
        href={item.url}
        download
        data-testid={`download-zip-dl-${filename}`}
        className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.25em] font-bold bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-3 py-2"
      >
        <DownloadSimple size={12} weight="bold" /> Download
      </a>
    </div>
  );
}

function VideoTile({ url, n }) {
  const filename = url.split("/").pop();
  return (
    <div
      data-testid={`promo-video-${n}`}
      className="border border-white/10 bg-black p-2 flex flex-col gap-1.5"
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF1B91]">#{String(n).padStart(2, "0")}</span>
        <span className="text-white/70 text-[11px] font-mono truncate">{filename}</span>
      </div>
      <video
        src={url}
        controls
        preload="none"
        className="w-full aspect-video bg-[#0A0A0A]"
        data-testid={`promo-video-player-${n}`}
      />
      <div className="flex items-center gap-1.5">
        <a
          href={url}
          target="_blank"
          rel="noreferrer noopener"
          data-testid={`promo-open-${n}`}
          className="flex-1 inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold border border-white/25 hover:border-[#FF1B91] text-white/80 hover:text-white px-2 py-1.5"
        >
          <ArrowSquareOut size={11} weight="bold" /> Open
        </a>
        <a
          href={url}
          download
          data-testid={`promo-dl-${n}`}
          className="flex-1 inline-flex items-center justify-center gap-1 text-[9px] uppercase tracking-[0.2em] font-bold bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-2 py-1.5"
        >
          <DownloadSimple size={11} weight="bold" /> DL
        </a>
      </div>
    </div>
  );
}

function CategoryHeading({ icon, label, count }) {
  return (
    <div className="flex items-center gap-2 mt-4 mb-2 first:mt-0">
      {icon}
      <h3 className="font-display font-black uppercase tracking-tight text-white text-base">{label}</h3>
      <span className="text-[10px] uppercase tracking-[0.25em] text-white/45 font-bold">· {count} file{count === 1 ? "" : "s"}</span>
    </div>
  );
}

export default function DownloadsResourcesPanel() {
  const promoVideos = useMemo(() => {
    // /sws-promo.mp4 (#1) then /sws-promo-2.mp4 … /sws-promo-28.mp4
    return Array.from({ length: 28 }, (_, i) => {
      const n = i + 1;
      return { n, url: n === 1 ? "/sws-promo.mp4" : `/sws-promo-${n}.mp4` };
    });
  }, []);

  return (
    <CollapsibleSection
      id="downloads-resources"
      testId="admin-downloads-resources"
      title={<><Folders size={20} weight="bold" className="text-[#FF1B91] inline mr-2" />Downloads &amp; Resources</>}
      subtitle="Admin-only · brand kits, IG carousels, promo videos, and other private business assets."
      defaultOpen={false}
    >
      <div className="space-y-6">

        <div>
          <CategoryHeading icon={<Package size={16} weight="fill" className="text-[#FF1B91]" />} label="Brand Kits" count={BRAND_KITS.length} />
          <div className="space-y-2">
            {BRAND_KITS.map((b) => <ZipRow key={b.url} item={b} />)}
          </div>
        </div>

        <div>
          <CategoryHeading icon={<InstagramLogo size={16} weight="fill" className="text-[#FF1B91]" />} label="Instagram Carousels" count={IG_CAROUSELS.length} />
          <div className="space-y-2">
            {IG_CAROUSELS.map((c) => <ZipRow key={c.url} item={c} />)}
          </div>
        </div>

        <div>
          <CategoryHeading icon={<FilmSlate size={16} weight="fill" className="text-[#FF1B91]" />} label="Promo Videos" count={promoVideos.length} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {promoVideos.map((v) => <VideoTile key={v.url} url={v.url} n={v.n} />)}
          </div>
        </div>

        <div>
          <CategoryHeading icon={<IdentificationBadge size={16} weight="fill" className="text-[#FF1B91]" />} label="Business Materials" count={BUSINESS_MATERIALS.length} />
          <div className="space-y-2">
            {BUSINESS_MATERIALS.map((b) => <ZipRow key={b.url} item={b} />)}
          </div>
        </div>

        <div>
          <CategoryHeading icon={<FilePdf size={16} weight="fill" className="text-[#FF1B91]" />} label="Other Private Resources" count={OTHER_PRIVATE_RESOURCES.length} />
          <div className="space-y-2">
            {OTHER_PRIVATE_RESOURCES.map((r) => <ZipRow key={r.url} item={r} />)}
          </div>
        </div>

      </div>
    </CollapsibleSection>
  );
}
